import { Link } from 'react-router-dom'

// ⚠️ Comparatif avec un produit tiers. On ne décrit de BlockerX que ce qui
// vaut pour TOUT bloqueur au niveau application (il vit dans les
// permissions du système, qu'on peut retirer). Rien de précis sur ses
// fonctions ni ses prix : ça change, et une erreur sur un concurrent nommé
// coûte cher. À vérifier sur leur site avant d'en dire plus.
const guide = {
  slug: 'blockerx-alternative',
  category: 'compare',
  kicker: 'Compare',
  title: 'BlockerX alternative: when an app-level blocker isn’t enough',
  seoTitle: 'BlockerX Alternative: A Lock You Can’t Remove',
  description: `App-level blockers like BlockerX live inside permissions you granted and can take back. The supervised-profile model, and how the two compare, honestly.`,
  published: '2026-08-25',
  modified: '2026-09-26',
  minutes: 5,
  intro: `If you're searching for a BlockerX alternative, there's a decent
          chance you already had it and took it off. Not because it was a
          bad app — because it was an app. This piece is about the
          difference between a blocker that lives in your permissions and
          one that lives below them.`,
  next: ['porn-blocker-you-cant-uninstall', 'how-to-block-porn-on-iphone', 'covenant-eyes-alternative'],
  body: (
    <>
      <h2>What an app-level blocker can and can't do</h2>
      <p>
        BlockerX and its peers are apps. On a phone, an app blocks by using
        permissions the system lets it ask for — a content filter, a
        VPN-style tunnel, accessibility access — and by adding friction
        around removal: a partner who must approve, a timer, a password.
        Features and prices change, so check their site for what's current.
      </p>
      <p>
        The structural point doesn't change: <b>what you granted, you can
        take back.</b> An app is deleted from the home screen. A permission
        is revoked in Settings. The friction an app adds is real, and it's
        the best an app can do — but it's friction, and friction is what
        gives at two in the morning. If you've removed a blocker before,
        you already know the gesture.
      </p>

      <h2>The level below the app</h2>
      <p>
        On iPhone and iPad there's a mechanism the app layer can't reach:
        <b> supervision</b>. It's what organisations use to hand out devices
        with settings that stick. On a supervised phone, a configuration
        profile can be non-removable — there's no Remove Profile button —
        and it can forbid adding a VPN. Nothing on the phone undoes it,
        and there's no password because there's none to guess.
      </p>
      <p>
        That's the level SELFCNTRL works at. The filter is a DNS-level
        category filter — pornography, nudity, adult themes refused for
        every app and every browser, private mode included. Supervision is
        what keeps it on. Coming off is a deliberate request from your
        account, after a cooling-off delay. Free to stop; unable to stop on
        an impulse. The full mechanism is in
        <Link to="/guides/porn-blocker-you-cant-uninstall"> what makes a blocker immovable</Link>.
      </p>

      <h2>What comes with it</h2>
      <ul>
        <li><b>Every device, one account</b> — iPhone, iPad, Mac, Windows.</li>
        <li><b>A lock that only tightens.</b> Adding a locked day or a curfew applies at once; loosening waits twenty-four hours; the floor never drops.</li>
        <li><b>Night curfews</b> that cut the internet on a schedule, per weekday, to the minute.</li>
        <li><b>No partner required.</b> No report, no shared dashboard, nothing read or stored — filtering is by category, so there's no history to keep.</li>
        <li><b>The pace you choose once.</b> Cut it dead, or four locked days a week climbing to seven in six weeks.</li>
        <li><b>$100 if you beat it.</b> Strip the filter off a device we locked and we pay you on top of a refund — <Link to="/remboursement">conditions</Link>.</li>
      </ul>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th>Question</th><th>App-level blocker</th><th>SELFCNTRL</th></tr>
        </thead>
        <tbody>
          <tr><td>Where it lives</td><td>In app permissions you granted</td><td>In a supervised profile below the app layer</td></tr>
          <tr><td>Removed from the phone by</td><td>Deleting the app / revoking a permission (with friction)</td><td>Not possible from the phone</td></tr>
          <tr><td>Adding a VPN to bypass</td><td>Depends on the platform</td><td>Forbidden by the profile on iPhone / iPad</td></tr>
          <tr><td>Needs a partner to hold</td><td>Often, for uninstall protection</td><td>No</td></tr>
          <tr><td>Reads your activity</td><td>Depends on features used</td><td>No — category filter, no history</td></tr>
          <tr><td>Android</td><td>Usually yes</td><td>Not today</td></tr>
          <tr><td>Price</td><td>See their site</td><td>$9.99/mo, or $49.99/year</td></tr>
        </tbody>
      </table>

      <h2>The honest limits</h2>
      <p>
        Two. SELFCNTRL doesn't run on Android today. And the lock that
        physically can't come off — the one we put $100 behind — is on
        iPhone and iPad, where Apple gives us supervision; on Mac and
        Windows you get the same DNS filter and curfews, without an
        equivalent of supervision, because none exists for a personal
        computer. If your device is an iPhone, the ten-minute setup is in
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is there a free version of BlockerX, or a free alternative?',
      a: `BlockerX's current plans are on its own site. Free filtering exists
          — Screen Time, public filtering DNS — and is worth turning on today.
          None of it is hard to switch off, which is the difference between
          a filter and a lock.`
    },
    {
      q: 'Why do I keep uninstalling porn blocker apps?',
      a: `Because they're apps, and apps are removed by the person holding
          the phone — on the night that person is you at 2am. The fix isn't
          more willpower; it's a block that lives below the app layer, where
          the phone itself can't remove it.`
    },
    {
      q: 'Does SELFCNTRL need an accountability partner to prevent uninstall?',
      a: `No. On iPhone and iPad the profile is non-removable through
          supervision, and taking it off is a request from your account with
          a cooling-off delay. No person holds a key.`
    },
    {
      q: 'Is it on Android?',
      a: `Not today: iPhone, iPad, Mac and Windows. The immovable lock relies
          on Apple's supervision, which is why it started there.`
    }
  ]
}

export default guide
